"use client";

/**
 * Client body for the Jira integration page. Lives apart from `page.tsx`
 * (which stays a server component for `metadata`) so it can call `useT()` and
 * follow the visitor's locale, exactly like the homepage. All copy comes from
 * the `intgJira` namespace; structure (icons, hrefs, capability statuses)
 * stays here.
 */

import {
  Search,
  ArrowRightLeft,
  Tag,
  MessageSquare,
  Reply,
  History,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  UseCaseHero,
  Section,
  SectionHeader,
  FeatureGrid,
  CapabilityGrid,
  Faq,
  SecurityNote,
  RelatedLinks,
  CtaBanner,
} from "@/components/marketing/marketing-kit";
import { useT } from "@/i18n";

export function JiraContent() {
  const t = useT();
  const k = (s: string) => `intgJira.${s}`;

  const features = [
    { key: "read", icon: <Search size={18} /> },
    { key: "transitions", icon: <ArrowRightLeft size={18} /> },
    { key: "labels", icon: <Tag size={18} /> },
    { key: "comments", icon: <MessageSquare size={18} /> },
    { key: "replies", icon: <Reply size={18} /> },
    { key: "audit", icon: <History size={18} /> },
  ].map((f) => ({
    icon: f.icon,
    title: t(k(`features.${f.key}.title`)),
    body: t(k(`features.${f.key}.body`)),
  }));

  const capabilities = [
    { key: "read", status: "supported" as const },
    { key: "transitions", status: "conditional" as const },
    { key: "labels", status: "supported" as const },
    { key: "comments", status: "conditional" as const },
    { key: "replies", status: "conditional" as const },
    { key: "singleStatus", status: "unavailable" as const },
  ].map((c) => ({
    name: t(k(`capabilities.${c.key}.name`)),
    status: c.status,
    body: t(k(`capabilities.${c.key}.body`)),
  }));

  const faq = ["q1", "q2", "q3", "q4", "q5"].map((q) => ({
    q: t(k(`faq.${q}.q`)),
    a: t(k(`faq.${q}.a`)),
  }));

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "/demo", label: t(k("hero.primaryCta")) }}
        secondary={{
          href: "/use-cases/slack-jira-ticket-questions",
          label: t(k("hero.secondaryCta")),
        }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      <Section>
        <SectionHeader
          eyebrow={t(k("whatItDoes.eyebrow"))}
          title={t(k("whatItDoes.title"))}
        />
        <FeatureGrid features={features} />
      </Section>

      <Section muted>
        <SectionHeader
          eyebrow={t(k("capabilitiesSection.eyebrow"))}
          title={t(k("capabilitiesSection.title"))}
          description={t(k("capabilitiesSection.description"))}
        />
        <CapabilityGrid
          capabilities={capabilities}
          statusLabels={{
            supported: t("mk.cap.supported"),
            conditional: t("mk.cap.conditional"),
            unavailable: t("mk.cap.unavailable"),
          }}
        />
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("failures.eyebrow"))} title={t(k("failures.title"))} />
        <SecurityNote icon={<History size={18} />} title={t(k("securityNote.title"))}>
          {t(k("securityNote.body"))}
        </SecurityNote>
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("faq.eyebrow"))} title={t(k("faq.title"))} />
        <Faq items={faq} />
      </Section>

      <Section>
        <RelatedLinks
          title={t("mk.relatedTitle")}
          links={[
            {
              href: "/use-cases/slack-jira-ticket-questions",
              label: t(k("related.ticketQuestions.label")),
              description: t(k("related.ticketQuestions.description")),
            },
            {
              href: "/use-cases/freshdesk-jira-approved-replies",
              label: t(k("related.approvedReplies.label")),
              description: t(k("related.approvedReplies.description")),
            },
            {
              href: "/integrations/slack",
              label: t(k("related.slack.label")),
              description: t(k("related.slack.description")),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/demo", label: t(k("cta.primary")) }}
        secondary={{ href: "/contact", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
